import { Camera, Download, ExternalLink, FileText, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorState, errorText, Field, Modal, Skeleton } from './index';

/** Types the API stores (the server reads the real one from the bytes). */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
/** What the file picker offers: any photo (the camera included) and PDF. */
export const DOCUMENT_FILE_ACCEPT = 'image/*,application/pdf';
const MAX_SIDE = 2000;
const RECOMPRESS_OVER = 1.5 * 1024 * 1024;

/**
 * Gets a picked file ready to upload: photos larger than needed (or in a type the API does not
 * take, such as HEIC where the browser can decode it) are redrawn as a JPEG of at most 2000 px;
 * PDFs travel as they are. Throws a sentence for the person when the file cannot be used.
 */
export async function prepareDocumentFile(file: File): Promise<Blob> {
  if (file.type === 'application/pdf') return file;
  if (!file.type.startsWith('image/')) throw new Error('Solo se admiten fotos y archivos PDF.');
  if (ACCEPTED.includes(file.type) && file.size <= RECOMPRESS_OVER) return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('No se pudo leer la foto. Tómala de nuevo o elige otra en JPG o PNG.');
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error('No se pudo preparar la foto. Inténtalo de nuevo.');
  return blob;
}

/** The file field of a document form: pick (or take) a photo or a PDF, see its name, remove it. */
export function DocumentFileInput({ value, onChange, label = 'Foto o PDF del documento', hint, required }: { value: File | null; onChange: (f: File | null) => void; label?: string; hint?: string; required?: boolean }) {
  const help = hint ?? 'Una foto nítida donde se lea todo, o el PDF. Máximo 8 MB.';
  return value ? (
    <Field label={label} optional={!required} hint={help} name="fileId">
      <div className="file-picked">
        <FileText size={18} aria-hidden />
        <span className="file-picked-name">{value.name}</span>
        <button type="button" className="btn ghost icon sm" aria-label={`Quitar ${value.name}`} onClick={() => onChange(null)}>
          <X size={16} aria-hidden />
        </button>
      </div>
    </Field>
  ) : (
    <Field label={label} optional={!required} hint={help} name="fileId">
      {/* the native control stays (keyboard, camera, required) but hidden: the button is its label */}
      <label className="btn file-pick">
        <input className="sr-only" type="file" accept={DOCUMENT_FILE_ACCEPT} required={required} onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
        <Camera size={18} aria-hidden /> Tomar o elegir foto o PDF
      </label>
    </Field>
  );
}

/**
 * Shows a document's file, fetched with the session (never by a public URL): photos inline, PDFs
 * framed where the browser can, always with «Abrir» and «Descargar».
 */
export function DocumentFileModal({ title, load, fileName, onClose }: { title: string; load: () => Promise<Blob>; fileName: string; onClose: () => void }) {
  const [state, setState] = useState<{ url: string; type: string } | { error: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let url: string | null = null;
    let live = true;
    setState(null);
    load().then(
      (blob) => {
        url = URL.createObjectURL(blob);
        if (live) setState({ url, type: blob.type });
      },
      (err) => live && setState({ error: errorText(err) }),
    );
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
    // `load` is a new function on every render of the caller: reload only on retry
  }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = state && 'url' in state && state.type === 'application/pdf';
  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      footer={
        state && 'url' in state ? (
          <>
            <a className="btn" href={state.url} target="_blank" rel="noopener">
              <ExternalLink size={16} aria-hidden /> Abrir
            </a>
            <a className="btn" href={state.url} download={`${fileName}${pdf ? '.pdf' : '.jpg'}`}>
              <Download size={16} aria-hidden /> Descargar
            </a>
          </>
        ) : undefined
      }
    >
      {!state ? (
        <Skeleton rows={4} label="Cargando el archivo" />
      ) : 'error' in state ? (
        <ErrorState message={state.error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : pdf ? (
        <iframe className="document-file-frame" src={state.url} title={title} />
      ) : (
        <img className="document-file-image" src={state.url} alt={title} />
      )}
    </Modal>
  );
}
